import {
  CalendarMonth as CalendarMonthIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  getISOWeek,
  isSameDay,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { sv } from "date-fns/locale";
import React, { useMemo, useState } from "react";
import { SalesManagerFilterParams } from "../../store/services/salesManagerService";

interface SalesManagerFiltersProps {
  filters: SalesManagerFilterParams;
  onFiltersChange: (filters: SalesManagerFilterParams) => void;
}

type RangeKey =
  | "today"
  | "last7"
  | "last30"
  | "last45"
  | "last90"
  | "next7"
  | "next30"
  | "next45"
  | "next90"
  | "currentYear"
  | "prevWeek"
  | "prevMonth"
  | "nextWeek"
  | "nextMonth";

// Svensk kalendervecka börjar på måndag
const WEEK_STARTS_ON = 1 as const;

const toDateKey = (date: Date): string => format(date, "yyyy-MM-dd");

// Beräknar konkreta from/to för en preset. Vi använder samma funktion för
// att sätta ranges via knappar OCH för att detektera vilken preset som
// matchar current state (för active-highlighting).
const computeRange = (range: RangeKey, referenceDate: Date): [Date, Date] => {
  switch (range) {
    case "today":
      return [startOfDay(referenceDate), endOfDay(referenceDate)];
    case "last7":
      return [startOfDay(subDays(referenceDate, 6)), endOfDay(referenceDate)];
    case "last30":
      return [startOfDay(subDays(referenceDate, 29)), endOfDay(referenceDate)];
    case "last45":
      return [startOfDay(subDays(referenceDate, 44)), endOfDay(referenceDate)];
    case "last90":
      return [startOfDay(subDays(referenceDate, 89)), endOfDay(referenceDate)];
    case "next7":
      return [startOfDay(referenceDate), endOfDay(addDays(referenceDate, 6))];
    case "next30":
      return [startOfDay(referenceDate), endOfDay(addDays(referenceDate, 29))];
    case "next45":
      return [startOfDay(referenceDate), endOfDay(addDays(referenceDate, 44))];
    case "next90":
      return [startOfDay(referenceDate), endOfDay(addDays(referenceDate, 89))];
    case "currentYear":
      return [startOfYear(referenceDate), endOfYear(referenceDate)];
    case "prevWeek": {
      const prev = subWeeks(referenceDate, 1);
      return [
        startOfWeek(prev, { weekStartsOn: WEEK_STARTS_ON }),
        endOfWeek(prev, { weekStartsOn: WEEK_STARTS_ON }),
      ];
    }
    case "prevMonth": {
      const prev = subMonths(referenceDate, 1);
      return [startOfMonth(prev), endOfMonth(prev)];
    }
    case "nextWeek": {
      const next = addWeeks(referenceDate, 1);
      return [
        startOfWeek(next, { weekStartsOn: WEEK_STARTS_ON }),
        endOfWeek(next, { weekStartsOn: WEEK_STARTS_ON }),
      ];
    }
    case "nextMonth": {
      const next = addMonths(referenceDate, 1);
      return [startOfMonth(next), endOfMonth(next)];
    }
  }
};

type RollingPreset = { key: RangeKey; label: string };

const ROLLING_BACK: RollingPreset[] = [
  { key: "today", label: "Idag" },
  { key: "last7", label: "7 dagar" },
  { key: "last30", label: "30 dagar" },
  { key: "last45", label: "45 dagar" },
  { key: "last90", label: "90 dagar" },
  { key: "currentYear", label: "Innevarande år" },
];

const ROLLING_FORWARD: RollingPreset[] = [
  { key: "today", label: "Idag" },
  { key: "next7", label: "7 dagar" },
  { key: "next30", label: "30 dagar" },
  { key: "next45", label: "45 dagar" },
  { key: "next90", label: "90 dagar" },
  { key: "currentYear", label: "Innevarande år" },
];

const ALL_ROLLING: RangeKey[] = [
  ...ROLLING_BACK.map((r) => r.key),
  ...ROLLING_FORWARD.map((r) => r.key),
];

// Parsar "yyyy-MM-dd" till en lokal Date (midnatt lokal tid)
const parseFilterDate = (value: string | undefined): Date | null => {
  if (!value) return null;
  try {
    return parseISO(value);
  } catch {
    return null;
  }
};

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "Maj", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dec",
];

// Beräkna start (måndag) och slut (söndag) för en given ISO-vecka
const getWeekRange = (year: number, week: number): [Date, Date] => {
  // 4 januari är alltid i ISO-vecka 1
  const jan4 = new Date(year, 0, 4);
  const week1Start = startOfWeek(jan4, { weekStartsOn: WEEK_STARTS_ON });
  const weekStart = addWeeks(week1Start, week - 1);
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: WEEK_STARTS_ON });
  return [startOfDay(weekStart), endOfDay(weekEnd)];
};

const SalesManagerFilters: React.FC<SalesManagerFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const theme = useTheme();
  const [customOpen, setCustomOpen] = useState(false);
  const [rollingDirection, setRollingDirection] = useState<"back" | "forward">("back");

  const fromDate = useMemo(
    () => parseFilterDate(filters.fromDate),
    [filters.fromDate]
  );
  const toDate = useMemo(
    () => parseFilterDate(filters.toDate),
    [filters.toDate]
  );

  const currentWeek = getISOWeek(new Date());
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const [pickerYear, setPickerYear] = useState(currentYear);
  // Visa 3 månader i taget, centrerat på nuvarande
  const [monthStart, setMonthStart] = useState(Math.max(0, currentMonth - 1));
  // Visa 5 veckor i taget, centrerat på nuvarande
  const [weekStart, setWeekStart] = useState(Math.max(1, currentWeek - 2));

  // Detektera vilken preset (om någon) matchar nuvarande range — används
  // för att ge ToggleButtons en aktiv-state. Vi jämför bara datumet, inte
  // tidskomponenten, eftersom backend ändå ignorerar tid.
  const activePreset: RangeKey | null = useMemo(() => {
    if (!fromDate || !toDate) return null;
    const today = new Date();
    for (const key of ALL_ROLLING) {
      const [presetFrom, presetTo] = computeRange(key, today);
      if (isSameDay(presetFrom, fromDate) && isSameDay(presetTo, toDate)) {
        return key;
      }
    }
    return null;
  }, [fromDate, toDate]);

  // Detektera om nuvarande filter matchar en specifik vecka eller månad
  const activeWeek = useMemo<number | null>(() => {
    if (!fromDate || !toDate) return null;
    for (let w = 1; w <= 53; w++) {
      const [ws, we] = getWeekRange(fromDate.getFullYear(), w);
      if (isSameDay(ws, fromDate) && isSameDay(we, toDate)) return w;
    }
    return null;
  }, [fromDate, toDate]);

  const activeMonth = useMemo<number | null>(() => {
    if (!fromDate || !toDate) return null;
    const ms = startOfMonth(fromDate);
    const me = endOfMonth(fromDate);
    if (isSameDay(ms, fromDate) && isSameDay(me, toDate)) {
      return fromDate.getMonth();
    }
    return null;
  }, [fromDate, toDate]);

  // Metadata för period-display: antal dagar + label för preset
  const periodMeta = useMemo(() => {
    if (!fromDate || !toDate) {
      return { days: 0, label: "Ingen period vald" };
    }
    const days = differenceInCalendarDays(toDate, fromDate) + 1;
    const allRanges = [...ROLLING_BACK, ...ROLLING_FORWARD];
    const preset = allRanges.find((r) => r.key === activePreset);
    let label = "Egen period";
    if (preset) {
      label = preset.label;
    } else if (activeWeek !== null) {
      label = `Vecka ${activeWeek}`;
    } else if (activeMonth !== null && fromDate) {
      label = MONTH_NAMES[activeMonth] + " " + fromDate.getFullYear();
    }
    return { days, label };
  }, [fromDate, toDate, activePreset, activeWeek, activeMonth]);

  const formattedRange = useMemo(() => {
    if (!fromDate || !toDate) return "";
    const sameYear = fromDate.getFullYear() === toDate.getFullYear();
    const fromStr = format(fromDate, sameYear ? "d MMM" : "d MMM yyyy", {
      locale: sv,
    });
    const toStr = format(toDate, "d MMM yyyy", { locale: sv });
    if (isSameDay(fromDate, toDate)) return toStr;
    return `${fromStr} – ${toStr}`;
  }, [fromDate, toDate]);

  const applyRange = (from: Date, to: Date) => {
    onFiltersChange({
      ...filters,
      fromDate: toDateKey(from),
      toDate: toDateKey(to),
    });
  };

  const setDateRange = (range: RangeKey) => {
    const [from, to] = computeRange(range, new Date());
    applyRange(from, to);
  };

  // Skifta hela perioden framåt eller bakåt med sin egen längd.
  // Fungerar för både presets och egna perioder.
  const shiftPeriod = (direction: 1 | -1) => {
    if (!fromDate || !toDate) return;
    const days = differenceInCalendarDays(toDate, fromDate) + 1;
    const newFrom = addDays(fromDate, days * direction);
    const newTo = addDays(toDate, days * direction);
    applyRange(startOfDay(newFrom), endOfDay(newTo));
  };

  const handleDateChange = (key: "fromDate" | "toDate", date: Date | null) => {
    onFiltersChange({
      ...filters,
      [key]: date ? toDateKey(date) : undefined,
    });
  };

  const clearFilters = () => {
    // Behåller userNos — det är en drawer-hanterad persistent lista.
    onFiltersChange({ userNos: filters.userNos });
  };

  const hasDateFilter = Boolean(filters.fromDate || filters.toDate);

  return (
    <Paper
      sx={{
        borderRadius: 3,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.05)}`,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        overflow: "hidden",
      }}
    >
      {/* Övre raden: period-display med stepper + action-knappar */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
            }}
          >
            <CalendarMonthIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ lineHeight: 1.2 }}
              noWrap
            >
              {formattedRange || "Välj period"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasDateFilter
                ? `${periodMeta.label} · ${periodMeta.days} dagar`
                : "Ingen period vald"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title="Föregående period">
            <span>
              <IconButton
                size="small"
                onClick={() => shiftPeriod(-1)}
                disabled={!hasDateFilter}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Nästa period">
            <span>
              <IconButton
                size="small"
                onClick={() => shiftPeriod(1)}
                disabled={!hasDateFilter}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
          {hasDateFilter && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 0.5, my: 0.5 }}
              />
              <Tooltip title="Rensa period">
                <IconButton size="small" onClick={clearFilters} color="default">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Box>

      {/* Snabbval — varje rad för sig */}
      <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Rullande med +/- toggle */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Rullande
            </Typography>
            <ToggleButtonGroup
              value={rollingDirection}
              exclusive
              onChange={(_, value: "back" | "forward" | null) => {
                if (value) setRollingDirection(value);
              }}
              size="small"
              sx={{
                height: 24,
                "& .MuiToggleButton-root": {
                  px: 1,
                  py: 0,
                  fontSize: "0.7rem",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "12px !important",
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  "&.Mui-selected": {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": { bgcolor: theme.palette.primary.dark },
                  },
                },
              }}
            >
              <ToggleButton value="back">− Bakåt</ToggleButton>
              <ToggleButton value="forward">+ Framåt</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <ToggleButtonGroup
            value={activePreset}
            exclusive
            onChange={(_, value: RangeKey | null) => {
              if (value) setDateRange(value);
            }}
            size="small"
            sx={{
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: "20px !important",
                px: 2,
                py: 0.5,
                textTransform: "none",
                fontWeight: 500,
                "&.Mui-selected": {
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": { bgcolor: theme.palette.primary.dark },
                },
              },
            }}
          >
            {(rollingDirection === "back" ? ROLLING_BACK : ROLLING_FORWARD).map(
              (r) => (
                <ToggleButton key={r.key} value={r.key}>
                  {r.label}
                </ToggleButton>
              )
            )}
          </ToggleButtonGroup>
        </Box>

        {/* Månad — 3 i taget */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600, mr: 1 }}
            >
              Månad
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                if (monthStart === 0) {
                  setPickerYear((y) => y - 1);
                  setMonthStart(9);
                } else {
                  setMonthStart((s) => Math.max(0, s - 1));
                }
              }}
              sx={{ p: 0.25 }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {[0, 1, 2].map((offset) => {
              const idx = monthStart + offset;
              if (idx > 11) return null;
              const isActive =
                activeMonth === idx &&
                fromDate?.getFullYear() === pickerYear;
              const isCurrent =
                idx === currentMonth && pickerYear === currentYear;
              return (
                <Chip
                  key={idx}
                  label={`${MONTH_NAMES[idx]} ${pickerYear}`}
                  size="small"
                  variant={isActive ? "filled" : "outlined"}
                  color={isActive ? "primary" : "default"}
                  onClick={() => {
                    const d = new Date(pickerYear, idx, 1);
                    applyRange(startOfMonth(d), endOfMonth(d));
                  }}
                  sx={{
                    fontWeight: isCurrent ? 700 : 500,
                    fontSize: "0.75rem",
                    borderColor: isCurrent && !isActive
                      ? theme.palette.primary.main
                      : undefined,
                    cursor: "pointer",
                  }}
                />
              );
            })}
            <IconButton
              size="small"
              onClick={() => {
                if (monthStart >= 9) {
                  setPickerYear((y) => y + 1);
                  setMonthStart(0);
                } else {
                  setMonthStart((s) => Math.min(9, s + 1));
                }
              }}
              sx={{ p: 0.25 }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Vecka — 5 i taget */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600, mr: 1 }}
            >
              Vecka
            </Typography>
            <IconButton
              size="small"
              onClick={() => setWeekStart((s) => Math.max(1, s - 1))}
              disabled={weekStart <= 1}
              sx={{ p: 0.25 }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {[0, 1, 2, 3, 4].map((offset) => {
              const w = weekStart + offset;
              if (w > 52) return null;
              const isActive =
                activeWeek === w &&
                fromDate?.getFullYear() === pickerYear;
              const isCurrent =
                w === currentWeek && pickerYear === currentYear;
              return (
                <Chip
                  key={w}
                  label={`v${w}`}
                  size="small"
                  variant={isActive ? "filled" : "outlined"}
                  color={isActive ? "primary" : "default"}
                  onClick={() => {
                    const [from, to] = getWeekRange(pickerYear, w);
                    applyRange(from, to);
                  }}
                  sx={{
                    fontWeight: isCurrent ? 700 : 500,
                    fontSize: "0.75rem",
                    borderColor: isCurrent && !isActive
                      ? theme.palette.primary.main
                      : undefined,
                    cursor: "pointer",
                  }}
                />
              );
            })}
            <IconButton
              size="small"
              onClick={() => setWeekStart((s) => Math.min(48, s + 1))}
              disabled={weekStart >= 48}
              sx={{ p: 0.25 }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Custom range — collapsed as default */}
      <Divider sx={{ opacity: 0.6 }} />
      <Box>
        <Button
          fullWidth
          onClick={() => setCustomOpen((prev) => !prev)}
          endIcon={customOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            justifyContent: "space-between",
            px: 3,
            py: 1.25,
            borderRadius: 0,
            color: "text.secondary",
            textTransform: "none",
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          EGEN PERIOD
        </Button>
        <Collapse in={customOpen} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3, pt: 0.5, pb: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
            >
              <DatePicker
                label="Från"
                value={fromDate}
                onChange={(date) => handleDateChange("fromDate", date)}
                format="d MMM yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
              <Box
                sx={{
                  color: "text.secondary",
                  display: { xs: "none", sm: "block" },
                  fontWeight: 500,
                }}
              >
                →
              </Box>
              <DatePicker
                label="Till"
                value={toDate}
                onChange={(date) => handleDateChange("toDate", date)}
                format="d MMM yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
              {hasDateFilter && (
                <Button
                  size="small"
                  variant="text"
                  onClick={clearFilters}
                  startIcon={<ClearIcon fontSize="small" />}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Rensa
                </Button>
              )}
            </Stack>
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default SalesManagerFilters;
